import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {retailerT} from '@/lib/types/retailer.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderRetailerTableStats} from '@/lib/config/retailerTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useMarket} from '@/pages/(shared)/market/providers/marketDetailsProvider';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent, SearchAndSelectComponent, StatsRowComponent} from '@/components';
import {DataTable, DataTableRefT} from '@/components/dataTable';

/**
 * Market Detail Retailers Component
 */
function MarketDetailRetailers() {
  const {toast} = useNotifications();
  const {isAdmin} = useAuth();
  const {marketId, searchRetailersToAdd, addRetailerToMarket, removeRetailerFromMarket} = useMarket();

  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<retailerT[]>([]);

  // Refs
  const dataTableRef = useRef<DataTableRefT>(null);
  const dataRowToDeleteRef = useRef<retailerT | null>(null);

  // TranStack Queries
  const {data: stats} = useViewQuery({
    view: 'retailer_stats',
  });

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    table: 'Market_Retailer',
    title: 'Retailers Summary',
    columns: ['fk_market_id', 'date_removed', ['Retailer', ['retailer_id', 'retailer_name', 'retailer_city']]],
    searchColumn: 'Retailer.retailer_name',
    filters: [{column: 'fk_market_id', operator: 'eq', value: marketId}],
    addButtonLabel: 'Add Retailer To Market',
    noResultsMessage: 'No retailers in market',
    preventScrolling: true,
    onAdd: () => setSearchModalVisible(true),
    columnsDisplay: [
      {header: 'ID', field: 'Retailer.retailer_id'},
      {
        header: 'Status',
        field: 'date_removed',
        render: row => (
          <span className={`badge bg-${!row.date_removed ? 'success' : 'danger'} text-white`}>
            {!row.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'Retailer.retailer_name',
        render: row => (
          <Link
            className="text-primary hover:text-primary dark:text-primary"
            to={`/retailers/${row.Retailer.retailer_id}`}
          >
            {row.Retailer.retailer_name}
          </Link>
        ),
      },
      {header: 'City', field: 'Retailer.retailer_city'},
    ],
    actionsDisplay: [
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: async row => {
          dataRowToDeleteRef.current = row.Retailer;
          setDeleteModalVisible(true);
        },
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = renderRetailerTableStats(stats?.total, stats?.active, stats?.inactive);

  // Functions
  // Function used to close the search modal
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchResults([]);
  };

  // Function used to get the search results
  const onSearch = async (query: string) => {
    const results = await searchRetailersToAdd(query);
    setSearchResults(results);
  };

  // Function used to add a row to the market
  const handleAddToMarket = async (retailer: retailerT) => {
    try {
      await addRetailerToMarket(retailer.retailer_id);
      toast.success({
        title: 'Retailer Added To Market',
        message: `Retailer ${retailer.retailer_name} added to market`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Adding To Market',
        message: (error as Error).message,
      });
    } finally {
      setSearchResults([]);
      setSearchModalVisible(false);
    }
  };

  // Function used to delete a row from the market
  const onDeleteConfirm = async () => {
    if (!dataRowToDeleteRef.current) throw new Error('Retailer not found');

    try {
      await removeRetailerFromMarket(dataRowToDeleteRef.current.retailer_id);
      toast.success({
        title: 'Retailer Removed From Market',
        message: `Retailer ${dataRowToDeleteRef.current.retailer_name} removed from market`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Removing Retailer From Market',
        message: (error as Error).message,
      });
    } finally {
      setDeleteModalVisible(false);
      dataTableRef.current?.refetch();
    }
  };

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable ref={dataTableRef} {...dataTableConfig} />
      <SearchAndSelectComponent
        title="Search and Select Retailers"
        isVisible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onSelect={handleAddToMarket}
        onSearch={onSearch}
        results={searchResults}
        mapStructure={{
          displayFields: [{title: 'Retailer Name', field: 'retailer_name'}],
          valueField: 'retailer_id',
        }}
      />
      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to remove this retailer from the market?"
        onConfirm={onDeleteConfirm}
      />
    </>
  );
}

export default MarketDetailRetailers;
